import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { usePlayerEventListener, usePlayerStore } from '@air/component-player';

import { useCurrentPlayerTimeGetter } from '~/components/AnnotationsV2/hooks/useCurrentPlayerTimeGetter';
import { isTimestampableClipType } from '~/components/AnnotationsV2/shared/isTimestampableClipType';
import { useVideoTimestampObserverContext } from '~/providers/VideoTimestampObserverProvider';
import { centralizedClipTypeSelector } from '~/store/centralizedClip/selectors';

type UseCapturedCommentTimestampArgs = {
  enabled: boolean;
};

export const useCapturedCommentTimestamp = ({ enabled }: UseCapturedCommentTimestampArgs) => {
  const clipType = useSelector(centralizedClipTypeSelector);
  const playerId = usePlayerStore((state) => state.id);
  const playing = usePlayerStore((state) => state.playing);
  const videoElement = usePlayerStore((state) => state.videoElement);
  const { subscribe } = useVideoTimestampObserverContext();
  const { getCurrentPlayerTime } = useCurrentPlayerTimeGetter();
  const [capturedTimestamp, setCapturedTimestamp] = useState(0);
  const wasPlayingRef = useRef(playing);
  const latestPlayingRef = useRef(playing);
  const canCaptureTimestamp = isTimestampableClipType(clipType);
  const hasMediaElement = !!videoElement;

  const captureCommentTimestamp = useCallback(() => {
    if (!enabled || !canCaptureTimestamp) {
      return;
    }

    setCapturedTimestamp(getCurrentPlayerTime());
  }, [canCaptureTimestamp, enabled, getCurrentPlayerTime]);

  const handleSeeked = useCallback(() => {
    if (playing) {
      return;
    }

    captureCommentTimestamp();
  }, [captureCommentTimestamp, playing]);

  usePlayerEventListener({
    enabled: enabled && canCaptureTimestamp,
    handler: handleSeeked,
    location: 'useCapturedCommentTimestamp',
    type: 'seeked',
  });

  useEffect(() => {
    latestPlayingRef.current = playing;
  }, [playing]);

  useEffect(() => {
    if (!playerId) {
      return;
    }

    wasPlayingRef.current = latestPlayingRef.current;
    setCapturedTimestamp(0);
  }, [playerId]);

  useEffect(() => {
    if (!enabled || !canCaptureTimestamp || hasMediaElement) {
      return;
    }

    captureCommentTimestamp();

    const { unsubscribe } = subscribe((currentTime) => {
      if (currentTime === null) {
        return;
      }

      setCapturedTimestamp(currentTime);
    });

    return unsubscribe;
  }, [canCaptureTimestamp, captureCommentTimestamp, enabled, hasMediaElement, subscribe]);

  useEffect(() => {
    if (!enabled || !canCaptureTimestamp || !hasMediaElement || playing) {
      return;
    }

    captureCommentTimestamp();
  }, [canCaptureTimestamp, captureCommentTimestamp, enabled, hasMediaElement, playing]);

  useEffect(() => {
    const justPaused = wasPlayingRef.current && !playing;
    wasPlayingRef.current = playing;

    if (justPaused) {
      captureCommentTimestamp();
    }
  }, [captureCommentTimestamp, playing]);

  return { captureCommentTimestamp, capturedTimestamp };
};
